// import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
// import TableChartIcon from "@mui/icons-material/TableChart";
import "jspdf-autotable";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, Legend, Label } from "recharts";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import { ChartColumnDecreasing } from "lucide-react"
import { TableOfContents } from "lucide-react"
import { useLoggedUser } from "../../../../../utils/useLoggedUser";
import { ValidDate } from "../../../../../utils/DateUtils";
import { isDirectWonLead } from "../../../../../utils/isDirectWonLead";

const SearchSalesGrossProfit = ({
  selectedYears,
  perdiem,
  averagecost,
  quotationProjects,
  actualQuotationProjects,
  calculatedProjects,
  packages,
  searchedLeadReports,
  searchedLeadReportswithoutyears,
  showTable,
  getProjectCost,
  getRowKey,
  getActualProjectCost,
  filterVersion,
  canViewTotalGrossProfitGraph = true,
  canViewTotalGrossProfitTable = true,
  canViewYtdGrossProfitGraph = true,
  canViewYtdGrossProfitTable = true,
  erpPlQuotedTotalsForGrossProfitCard,
  /** Numeric P&L totals from parent — primitives avoid stale object identity / wrong branch totals (~64L). */
  erpPlValueSumForCard,
  erpPlCostSumForCard,
  /** Σ row GP as rendered in P&L (itemized-aware); not always Value−Cost. */
  erpPlGrossProfitSumForCard,
  /** P&L Quoted Expenses cell formatter (projectKey → string). When set, YTD Cost (non-CRM Quoted) uses this instead of getProjectCost. */
  erpPlShowQuotedExpenseForRow,
  /** Actual P&L table totals (Value/Cost/GP) — source of truth for Actual Gross Profit cards. */
  actualPlCardTotals,
  /** Optional (CRM): final P&L table rows to use for Total Gross Profit only. */
  /** Optional (CRM): per-row accessors that match the P&L table columns exactly. */
  plGetQuotedValueForRow,
  plGetQuotedExpenseForRow,
  /** ERP Actual: Quoted Price per row from Actual P&L table. */
  plGetActualQuotedValueForRow,
  /** ERP Actual: same row visibility as Actual P&L table. */
  shouldShowActualPlRow,
  /** ERP Actual: precomputed totals from GP filters + Actual-visible rows. */
  erpActualValueSumForCard,
  erpActualCostSumForCard,
  erpActualGrossProfitSumForCard,
}) => {
  const popupRef = useRef(null);
  const USAComma = CommaConvert();
  const currency = useCurrencySymbol();
  const loggedUser = useLoggedUser()
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [probabilityTab, setProbabilityTab] = useState("graph");
  const [YTD, setYTD] = useState("graph");
  useEffect(() => {
    if (probabilityTab === "graph" && !canViewTotalGrossProfitGraph && canViewTotalGrossProfitTable) {
      setProbabilityTab("table");
    } else if (probabilityTab === "table" && !canViewTotalGrossProfitTable && canViewTotalGrossProfitGraph) {
      setProbabilityTab("graph");
    }
  }, [probabilityTab, canViewTotalGrossProfitGraph, canViewTotalGrossProfitTable]);

  useEffect(() => {
    if (YTD === "graph" && !canViewYtdGrossProfitGraph && canViewYtdGrossProfitTable) {
      setYTD("table");
    } else if (YTD === "table" && !canViewYtdGrossProfitTable && canViewYtdGrossProfitGraph) {
      setYTD("graph");
    }
  }, [YTD, canViewYtdGrossProfitGraph, canViewYtdGrossProfitTable]);

  const [ytdtotalValueCurrent, setYTDtotalValueCurrent] = useState("");
  const [ytdtotalProductCostCurrent, setYTDtotalProductCostCurrent] = useState("");
  const [ytdtotalGrossProfitCurrent, setYTDtotalGrossProfitCurrent] = useState("");

  const [totalValue, settotalValue] = useState("");
  const [totalProductCost, settotalProductCost] = useState();
  const [totalGrossProfit, settotalGrossProfit] = useState()
  const [filteredLeadByYear, setFilteredLeadByYear] = useState([])

  const countryCode = loggedUser?.countrycode
  // Keep in sync with FinanceGraph.jsx: ERP subscriptions must not use the CRM lead-total path.
  const pkgNormalized = String(packages || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  const isCrmPackage =
    pkgNormalized === "crm" ||
    pkgNormalized.includes("crm") ||
    (pkgNormalized.length > 0 && !pkgNormalized.includes("erp")) ||
    pkgNormalized.length === 0;
  const isErpPackage = pkgNormalized.includes("erp");
  // ERP Actual GP: use Gross Profit section filters (searchedLeadReports), not Actual P&L table totals.
  const useActualPlTableTotalsForGp =
    showTable === "Actual" &&
    !isErpPackage &&
    actualPlCardTotals &&
    Number(actualPlCardTotals.rowCount) > 0 &&
    Number.isFinite(Number(actualPlCardTotals.valueSum));
  const parseExpenseValue = (v) => parseFloat(String(v || "0").replace(/[^0-9.-]/g, "")) || 0;
  const parsePlExpenseStr = (s) => parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;
  const parseMoney = (v) => parseFloat(String(v || "0").replace(/,/g, "")) || 0;

  /** ERP Actual: Value from Actual P&L Quoted Price column (not inflated lead.QuotedValue). */
  const getErpActualQuotedValueForRow = useCallback(
    (row) => {
      if (typeof plGetActualQuotedValueForRow === "function" && getRowKey) {
        const fromPl = Number(plGetActualQuotedValueForRow(getRowKey(row)));
        if (Number.isFinite(fromPl) && fromPl !== 0) return fromPl;
      }
      const price = parseMoney(
        row?.quotedPrice ?? row?.quoted_price ?? row?.QuotedPrice
      );
      if (price !== 0) return price;
      return parseMoney(row?.QuotedValue);
    },
    [plGetActualQuotedValueForRow, getRowKey]
  );

  const getErpActualCostForRow = useCallback(
    (row) => {
      if (typeof getActualProjectCost === "function" && getRowKey) {
        return Number(getActualProjectCost(getRowKey(row))) || 0;
      }
      return 0;
    },
    [getActualProjectCost, getRowKey]
  );
  const getCrmQuotedValue = useCallback(
    (r) => {
      // Never use QuotedValue aggregate for CRM Gross Profit Value.
      return (
        parseMoney(
          r?.quotedPrice ?? r?.quoted_price ?? r?.QuotedPrice ?? r?.quotedvalue ?? 0
        ) || 0
      );
    },
    []
  );
  const getCrmQuotedExpense = useCallback(
    (r) =>
      parseExpenseValue(
        r?.quotedExpense ??
          r?.QuotedExpense ??
          r?.quotedExpenses ??
          r?.QuotedExpenses ??
          r?.quoted_expense ??
          r?.Quoted_Expense ??
          0
      ),
    []
  );

  const formatNumberByCountry = (num) => {
    if (num === null || num === undefined || num === "") return "";
    const value = Number(num);

    // 🇮🇳 India (+91)
    if (countryCode === "+91") {
      if (value >= 1_00_00_000) {
        const v = value / 1_00_00_000;
        return `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(v)}Cr`;
      } else if (value >= 1_00_000) {
        const v = value / 1_00_000;
        return `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(v)}L`;
      }
      return value.toLocaleString("en-IN");
    }

    // 🇺🇸 US (+1)
    if (countryCode === "+1") {
      if (value >= 1_000_000_000) {
        const v = value / 1_000_000_000;
        return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(v)}B`;
      } else if (value >= 1_000_000) {
        const v = value / 1_000_000;
        return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(v)}M`;
      }
      return value.toLocaleString("en-US");
    }

    return value.toLocaleString();
  };

  // useEffect(() => {
  //   console.log("filteredLeadByYear selectedYears", selectedYears);

  //   const normalizedYears = selectedYears?.map(Number);
  //   console.log("filteredLeadByYear normalizedYears", normalizedYears);

  //   const filteredLeadsByYear = searchedLeadReports?.filter((lead) => {
  //     const leadYear = new Date(lead.LeadDate).getFullYear();
  //     return normalizedYears?.includes(leadYear);
  //   });
  //   console.log("filteredLeadByYear filteredLeadsByYear", filteredLeadsByYear);

  //   const today = new Date();
  //   const currentMonth = today.getMonth(); // 0-based
  //   const currentDate = today.getDate();

  //   const ytdFilteredLeads = filteredLeadsByYear?.filter((lead) => {
  //     const leadDate = new Date(lead.LeadDate);
  //     const leadYear = leadDate.getFullYear();

  //     const startDate = new Date(leadYear, 0, 1); // Jan 1 of lead year
  //     const endDate = new Date(leadYear, currentMonth, currentDate); // same MM-DD as today in that year

  //     return leadDate >= startDate && leadDate <= endDate;
  //   });

  //   console.log("filteredLeadByYear ytdFilteredLeads", ytdFilteredLeads);


  //   setFilteredLeadByYear(ytdFilteredLeads)
  // }, [searchedLeadReports, selectedYears])


  useEffect(() => {
    let baseLeads = searchedLeadReportswithoutyears || [];

    const isActual = showTable === "Actual";
    const activeProjects = isActual ? (actualQuotationProjects || []) : (quotationProjects || []);

    // CRM: full filtered lead dataset. ERP Actual: same — do not project-gate (misses direct-won / P&L rows).
    if (!isCrmPackage && showTable !== "Actual" && activeProjects.length > 0) {
      const validProjectIds = new Set(activeProjects.map((p) => String(p.quotationId || "").trim()));
      const validProjectNames = new Set(activeProjects.map((p) => p.ProjectName));

      baseLeads = baseLeads.filter((lead) => {
        const qId = String(lead.quotationId || "").trim();
        if (lead.quotationId) {
          return validProjectIds.has(qId);
        }
        return validProjectNames.has(lead.ProjectName);
      });
    }

    if (!selectedYears || selectedYears.length === 0) {
      setFilteredLeadByYear(baseLeads);
      return;
    }

    const selectedPeriod = String(selectedYears?.[0] ?? "").trim();
    const yearOnlyMatch = selectedPeriod.match(/^(\d{4})$/);
    const monthMatch = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)$/);
    const quarterMatch = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)\s*-\s*([A-Za-z]+)$/);

    const selectedYear =
      yearOnlyMatch?.[1] ? Number(yearOnlyMatch[1]) :
        monthMatch?.[1] ? Number(monthMatch[1]) :
          quarterMatch?.[1] ? Number(quarterMatch[1]) :
            null;

    if (!selectedYear) {
      setFilteredLeadByYear(baseLeads);
      return;
    }

    const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    let startDate = new Date(selectedYear, 0, 1);
    let endDate;

    if (monthMatch) {
      const mIdx = getMonthIndex(monthMatch[2]);
      if (mIdx !== -1) {
        startDate = new Date(selectedYear, mIdx, 1);
        endDate = endOfDay(new Date(selectedYear, mIdx + 1, 0));
      }
    } else if (quarterMatch) {
      const startIdx = getMonthIndex(quarterMatch[2]);
      const endIdx = getMonthIndex(quarterMatch[3]);
      if (startIdx !== -1 && endIdx !== -1) {
        startDate = new Date(selectedYear, startIdx, 1);
        endDate = endOfDay(new Date(selectedYear, endIdx + 1, 0));
      }
    }

    if (!endDate) {
      endDate = endOfDay(new Date(selectedYear, 11, 31));
    }

    // CRM Subscription Package requirement (Gross Profit section only):
    // - Total Gross Profit: ALL leads in selected year (handled upstream)
    // - YTD Gross Profit: for year 2025 only, cutoff is 8 May 2025 (inclusive)
    // IMPORTANT: Gross Profit section filters remain independent from P&L filters.
    let rangeEnd = endDate;
    if (isCrmPackage && yearOnlyMatch && selectedYear === 2025) {
      rangeEnd = endOfDay(new Date(2025, 4, 8)); // 8 May 2025 (month is 0-based)
    }

    const parseLeadDate = (lead) => {
      const raw = lead?.LeadDate ?? lead?.leadDate;
      if (!raw) return null;
      const normalized = ValidDate(raw);
      if (normalized) {
        const [y, m, d] = normalized.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return isNaN(dt.getTime()) ? null : dt;
      }
      const dt = new Date(raw);
      return isNaN(dt.getTime()) ? null : dt;
    };

    console.log("[YTD GP DEBUG] selectedPeriod:", selectedPeriod, "startDate:", startDate.toISOString(), "endDate:", endDate.toISOString(), "rangeEnd (YTD cap):", rangeEnd.toISOString());
    console.log("[YTD GP DEBUG] baseLeads count:", baseLeads.length, "sample LeadDates:", baseLeads.slice(0, 3).map(l => l.LeadDate));

    const ytdLeads = baseLeads.filter((lead) => {
      const leadDate = parseLeadDate(lead);
      if (!leadDate) {
        console.warn(
          "[YTD GP DEBUG] Unparseable LeadDate:",
          JSON.stringify(lead?.LeadDate ?? lead?.leadDate),
          "for",
          lead.ProjectName
        );
        return false;
      }
      const inRange = leadDate >= startDate && leadDate <= rangeEnd;
      if (!inRange) {
        console.log("[YTD GP DEBUG] Excluded:", lead.ProjectName, "raw:", lead.LeadDate, "parsed:", leadDate.toISOString(), "range:", startDate.toISOString(), "to", rangeEnd.toISOString());
      }
      return inRange;
    });

    console.log("[YTD GP DEBUG] ytdLeads count:", ytdLeads.length);
    setFilteredLeadByYear(ytdLeads);
  }, [searchedLeadReportswithoutyears, selectedYears, quotationProjects, actualQuotationProjects, showTable, isCrmPackage]);

  // Helper function to convert month name to index (0-11)
  const getMonthIndex = (monthName) => {
    const months = {
      jan: 0, january: 0,
      feb: 1, february: 1,
      mar: 2, march: 2,
      apr: 3, april: 3,
      may: 4,
      jun: 5, june: 5,
      jul: 6, july: 6,
      aug: 7, august: 7,
      sep: 8, september: 8,
      oct: 9, october: 9,
      nov: 10, november: 10,
      dec: 11, december: 11
    };

    return months[monthName.toLowerCase()] !== undefined
      ? months[monthName.toLowerCase()]
      : -1;
  };

  const applyPandlTotalsToCard = useCallback(() => {
    if (showTable === "Actual") return false;
    // CRM: use filter-independent year totals from FinanceGraph (Quoted Price / Cost / GP),
    // never local lead.QuotedValue aggregates that inflate Value.
    if (isCrmPackage) {
      if (
        erpPlValueSumForCard == null ||
        !Number.isFinite(Number(erpPlValueSumForCard)) ||
        erpPlCostSumForCard == null ||
        !Number.isFinite(Number(erpPlCostSumForCard)) ||
        erpPlGrossProfitSumForCard == null ||
        !Number.isFinite(Number(erpPlGrossProfitSumForCard))
      ) {
        return false;
      }
      const v = Number(erpPlValueSumForCard);
      const c = Number(erpPlCostSumForCard);
      const gp = Number(erpPlGrossProfitSumForCard);
      settotalValue(v);
      settotalProductCost(c);
      settotalGrossProfit(gp);
      return true;
    }
    const vRaw =
      erpPlValueSumForCard !== undefined && erpPlValueSumForCard !== null
        ? erpPlValueSumForCard
        : erpPlQuotedTotalsForGrossProfitCard?.valueSum;
    if (vRaw == null || !Number.isFinite(Number(vRaw))) return false;
    const v = Number(vRaw);
    let c = Number(
      erpPlCostSumForCard !== undefined && erpPlCostSumForCard !== null
        ? erpPlCostSumForCard
        : erpPlQuotedTotalsForGrossProfitCard?.costSum
    );
    if (!Number.isFinite(c)) c = 0;
    const gpRaw =
      erpPlGrossProfitSumForCard !== undefined && erpPlGrossProfitSumForCard !== null
        ? erpPlGrossProfitSumForCard
        : erpPlQuotedTotalsForGrossProfitCard?.grossProfitSum;
    const gp =
      gpRaw != null && Number.isFinite(Number(gpRaw)) ? Number(gpRaw) : v - c;
    settotalValue(v);
    settotalProductCost(c);
    settotalGrossProfit(gp);
    return true;
  }, [
    showTable,
    isCrmPackage,
    erpPlValueSumForCard,
    erpPlCostSumForCard,
    erpPlGrossProfitSumForCard,
    erpPlQuotedTotalsForGrossProfitCard,
  ]);

  useLayoutEffect(() => {
    applyPandlTotalsToCard();
  }, [applyPandlTotalsToCard]);

  useEffect(() => {
    var parseNum = function(v) { return parseFloat(String(v || "0").replace(/,/g, "")) || 0; };
    var allLeads = searchedLeadReports || [];

    if (showTable === "Actual") {
      settotalValue(0);
      settotalProductCost(0);
      settotalGrossProfit(0);

      // Source of truth: Actual P&L table totals (non-ERP only — ERP GP filters are independent).
      if (useActualPlTableTotalsForGp) {
        const v = Number(actualPlCardTotals.valueSum) || 0;
        const c = Number(actualPlCardTotals.costSum) || 0;
        const gp =
          actualPlCardTotals.grossProfitSum != null &&
          Number.isFinite(Number(actualPlCardTotals.grossProfitSum))
            ? Number(actualPlCardTotals.grossProfitSum)
            : v - c;
        settotalValue(v);
        settotalProductCost(c);
        settotalGrossProfit(gp);
        return;
      }

      // ERP Actual: GP-filtered leads that appear on Actual P&L (not all year leads with QuotedValue).
      if (isErpPackage) {
        var erpActualRows = allLeads;
        if (typeof shouldShowActualPlRow === "function") {
          erpActualRows = allLeads.filter(function(r) {
            return shouldShowActualPlRow(r);
          });
        }

        if (erpActualRows.length === 0) {
          settotalValue(0);
          settotalProductCost(0);
          settotalGrossProfit(0);
          return;
        }

        if (
          erpActualValueSumForCard != null &&
          Number.isFinite(Number(erpActualValueSumForCard)) &&
          erpActualCostSumForCard != null &&
          Number.isFinite(Number(erpActualCostSumForCard))
        ) {
          var vPre = Number(erpActualValueSumForCard);
          var cPre = Number(erpActualCostSumForCard);
          var gpPre =
            erpActualGrossProfitSumForCard != null &&
            Number.isFinite(Number(erpActualGrossProfitSumForCard))
              ? Number(erpActualGrossProfitSumForCard)
              : vPre - cPre;
          settotalValue(vPre);
          settotalProductCost(cPre);
          settotalGrossProfit(gpPre);
          return;
        }

        var erpTotalQV = 0;
        var erpTotalAC = 0;
        erpActualRows.forEach(function(r) {
          erpTotalQV += getErpActualQuotedValueForRow(r);
          erpTotalAC += getErpActualCostForRow(r);
        });
        settotalValue(erpTotalQV);
        settotalProductCost(erpTotalAC);
        settotalGrossProfit(erpTotalQV - erpTotalAC);
        return;
      }

      // Fallback when Actual P&L totals are not ready yet.
      if (!getRowKey || allLeads.length === 0) return;

      var actualLeads = allLeads.filter(function(lead) {
        var cost = getErpActualCostForRow(lead);
        return cost !== 0 || isDirectWonLead(lead);
      });

      if (actualLeads.length === 0) return;

      var totalQuotedValue = 0;
      var totalActualCost = 0;
      actualLeads.forEach(function(r) {
        totalQuotedValue += getErpActualQuotedValueForRow(r);
        totalActualCost += getErpActualCostForRow(r);
      });

      settotalValue(totalQuotedValue);
      settotalProductCost(totalActualCost);
      settotalGrossProfit(totalQuotedValue - totalActualCost);
      return;
    }

    if (applyPandlTotalsToCard()) return;

    settotalValue(0);
    settotalProductCost(0);
    settotalGrossProfit(0);

    // CRM requirement: Value must come from full filtered dataset (all rows),
    // not only rows with non-zero calculated cost.
    if (isCrmPackage) {
      // IMPORTANT (CRM Subscription Package only):
      // Total Gross Profit card must be computed ONLY from the Gross Profit section filtered CRM leads
      // of the selected YEAR (do not use any global/dashboard/P&L totals).
      //
      // Use the "without-years" list (already filtered by Gross Profit GROUP/CLIENT),
      // then apply *only* the selected year filter here.
      const selectedYear = (() => {
        const s = String((selectedYears && selectedYears[0]) || "").trim();
        const m = s.match(/^(\d{4})/);
        return m ? Number(m[1]) : null;
      })();

      const parseLeadDate = (lead) => {
        const raw =
          lead?.lead_date ??
          lead?.LeadDate ??
          lead?.leadDate ??
          lead?.leadDateTime;
        if (!raw) return null;
        const normalized = ValidDate(raw);
        if (normalized) {
          const [y, m, d] = normalized.split("-").map(Number);
          const dt = new Date(y, m - 1, d);
          return isNaN(dt.getTime()) ? null : dt;
        }
        const dt = new Date(raw);
        return isNaN(dt.getTime()) ? null : dt;
      };

      const totalGrossProfitLeads = (searchedLeadReportswithoutyears || []).filter((lead) => {
        if (!selectedYear) return true;
        const dt = parseLeadDate(lead);
        if (!dt) return false;
        return dt.getFullYear() === Number(selectedYear);
      });

      const getCrmQuotedExpenseNumber = (r) => {
        // Prefer explicit per-lead expense fields for CRM totals (no shared/global totals).
        const raw =
          r?.quoted_expenses ??
          r?.quotedExpenses ??
          r?.QuotedExpenses ??
          r?.quoted_expense ??
          r?.quotedExpense ??
          r?.QuotedExpense ??
          0;
        return parseExpenseValue(raw);
      };

      var totalQuotedValueCrm = 0;
      var totalQuotedExpensesCrm = 0;
      totalGrossProfitLeads.forEach(function(r) {
        totalQuotedValueCrm += getCrmQuotedValue(r);
        totalQuotedExpensesCrm += getCrmQuotedExpenseNumber(r);
      });

      var totalGrossProfitCrm = totalQuotedValueCrm - totalQuotedExpensesCrm;
      settotalValue(totalQuotedValueCrm);
      settotalProductCost(totalQuotedExpensesCrm);
      settotalGrossProfit(totalGrossProfitCrm);

      // Validation rule: Gross Profit must always be Value - Cost.
      if (Math.round(totalGrossProfitCrm) !== Math.round(totalQuotedValueCrm - totalQuotedExpensesCrm)) {
        console.warn("[CRM GP VALIDATION] Total Gross Profit mismatch detected.");
      }
      return;
    }

    // ERP / Quoted-expense style (non-CRM): Value = Σ quoted price for every visible lead — same as P&L column,
    // not only rows with non-zero calculated cost (that under-counted Value on ERP packages).
    if (!getProjectCost || !getRowKey || allLeads.length === 0) return;

    var totalQV = 0;
    var totalC = 0;
    const parsePlExpenseStrLocal = function(s) {
      return parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;
    };
    allLeads.forEach(function(r) {
      totalQV += parseNum(r.quotedPrice || r.QuotedValue);
      var rk = getRowKey(r);
      // ERP Quoted: Cost must reflect the P&L "Quoted Expenses" column when available.
      totalC +=
        typeof erpPlShowQuotedExpenseForRow === "function"
          ? parsePlExpenseStrLocal(erpPlShowQuotedExpenseForRow(rk))
          : getProjectCost(rk);
    });

    settotalValue(totalQV);
    settotalProductCost(totalC);
    settotalGrossProfit(totalQV - totalC);

  }, [
    showTable,
    packages,
    isCrmPackage,
    calculatedProjects,
    averagecost,
    perdiem,
    quotationProjects,
    actualQuotationProjects,
    searchedLeadReports,
    searchedLeadReportswithoutyears,
    selectedYears,
    getProjectCost,
    getActualProjectCost,
    getRowKey,
    applyPandlTotalsToCard,
    erpPlShowQuotedExpenseForRow,
    getCrmQuotedExpense,
    getCrmQuotedValue,
    actualPlCardTotals,
    isErpPackage,
    useActualPlTableTotalsForGp,
    getErpActualQuotedValueForRow,
    getErpActualCostForRow,
    shouldShowActualPlRow,
    erpActualValueSumForCard,
    erpActualCostSumForCard,
    erpActualGrossProfitSumForCard,
  ]);

  //for YTD
  useEffect(() => {
    setYTDtotalValueCurrent(0);
    setYTDtotalProductCostCurrent(0);
    setYTDtotalGrossProfitCurrent(0);

    // CRM: When we have the exact P&L table rows + accessors, compute YTD directly from them
    // and filter by the Gross Profit YEAR (fixes "YTD showing 2025 totals when year=2026").
    const selectedPeriodForCrmYtd = String((selectedYears && selectedYears[0]) || "").trim();
    const yearOnlyMatchForCrmYtd = selectedPeriodForCrmYtd.match(/^(\d{4})$/);
    const selectedYearForCrmYtd = yearOnlyMatchForCrmYtd ? Number(yearOnlyMatchForCrmYtd[1]) : null;
    const hasAccessorsForCrmYtd =
      isCrmPackage &&
      showTable !== "Actual" &&
      typeof plGetQuotedValueForRow === "function" &&
      typeof plGetQuotedExpenseForRow === "function" &&
      selectedYearForCrmYtd != null;

    if (hasAccessorsForCrmYtd) {
      const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
      const cutoff = selectedYearForCrmYtd === 2025 ? endOfDay(new Date(2025, 4, 8)) : null; // 8 May 2025

      const getLeadDate = (lead) => {
        const raw =
          lead?.lead_date ||
          lead?.leadDate ||
          lead?.LeadDate ||
          lead?.leadDateTime ||
          lead?.created_at ||
          lead?.createdAt;
        if (!raw) return null;
        const normalized = ValidDate(raw);
        if (normalized) {
          const [y, m, d] = normalized.split("-").map(Number);
          const dt = new Date(y, m - 1, d);
          return isNaN(dt.getTime()) ? null : dt;
        }
        const dt = new Date(raw);
        return isNaN(dt.getTime()) ? null : dt;
      };

      const ytdRows = (searchedLeadReportswithoutyears || []).filter((row) => {
        const dt = getLeadDate(row);
        if (!dt) return false;
        if (dt.getFullYear() !== selectedYearForCrmYtd) return false;
        if (cutoff) return dt <= cutoff;
        return true;
      });

      const ytdValue = ytdRows.reduce(
        (sum, row) => sum + (Number(plGetQuotedValueForRow(row)) || 0),
        0
      );
      const ytdCost = ytdRows.reduce((sum, row) => sum + (Number(plGetQuotedExpenseForRow(row)) || 0), 0);
      const ytdGp = ytdValue - ytdCost;

      setYTDtotalValueCurrent(ytdValue);
      setYTDtotalProductCostCurrent(ytdCost);
      setYTDtotalGrossProfitCurrent(ytdGp);
      return;
    }

    // CRM current year (year-only): YTD cards should match Total cards for the current year.
    // This keeps the UI consistent without tying Gross Profit filters to the P&L filters.
    const selectedPeriodForYtdCrm = String((selectedYears && selectedYears[0]) || "").trim();
    const yearOnlyMatchForYtdCrm = selectedPeriodForYtdCrm.match(/^(\d{4})$/);
    const selectedYearForYtdCrm = yearOnlyMatchForYtdCrm ? Number(yearOnlyMatchForYtdCrm[1]) : null;
    const isCurrentYearOnlyCrm =
      isCrmPackage &&
      showTable !== "Actual" &&
      selectedYearForYtdCrm != null &&
      selectedYearForYtdCrm === new Date().getFullYear();
    // Exception: year 2025 has a fixed cutoff (8 May 2025), so do not mirror Total.
    if (isCurrentYearOnlyCrm && selectedYearForYtdCrm !== 2025) {
      setYTDtotalValueCurrent(Number(totalValue) || 0);
      setYTDtotalProductCostCurrent(Number(totalProductCost) || 0);
      setYTDtotalGrossProfitCurrent(Number(totalGrossProfit) || 0);
      return;
    }

    // ERP subscription (Quoted): YTD Value/Cost/GP must reflect the P&L table totals
    // for the current year (year-only selection). This avoids mismatches where the
    // GP module computes YTD from LeadDate filtering while P&L is the source of truth.
    const selectedPeriodForYtd = String((selectedYears && selectedYears[0]) || "").trim();
    const yearOnlyMatchForYtd = selectedPeriodForYtd.match(/^(\d{4})$/);
    const selectedYearForYtd = yearOnlyMatchForYtd ? Number(yearOnlyMatchForYtd[1]) : null;
    const isCurrentYearOnly =
      selectedYearForYtd != null && selectedYearForYtd === new Date().getFullYear();
    const hasPandlTotalsForCard =
      showTable !== "Actual" &&
      erpPlValueSumForCard != null &&
      Number.isFinite(Number(erpPlValueSumForCard)) &&
      erpPlCostSumForCard != null &&
      Number.isFinite(Number(erpPlCostSumForCard)) &&
      erpPlGrossProfitSumForCard != null &&
      Number.isFinite(Number(erpPlGrossProfitSumForCard));

    if (isCurrentYearOnly && hasPandlTotalsForCard) {
      const v = Number(erpPlValueSumForCard);
      const c = Number(erpPlCostSumForCard);
      const gp = Number(erpPlGrossProfitSumForCard);
      setYTDtotalValueCurrent(v);
      setYTDtotalProductCostCurrent(c);
      setYTDtotalGrossProfitCurrent(gp);
      return;
    }

    var parseNum = function(v) { return parseFloat(String(v || "0").replace(/,/g, "")) || 0; };
    var allYearLeads = filteredLeadByYear || [];
    var selectedPeriod = String((selectedYears && selectedYears[0]) || "").trim();
    var monthMatchYTD = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)$/);
    var quarterMatchYTD = selectedPeriod.match(/^(\d{4})\s+([A-Za-z]+)\s*-\s*([A-Za-z]+)$/);

    var ytdOnly;
    if (isCrmPackage) {
      // CRM: filteredLeadByYear is already capped at today within the selected period (see effect above).
      ytdOnly = allYearLeads;
    } else if (monthMatchYTD || quarterMatchYTD) {
      ytdOnly = allYearLeads;
    } else {
      var today = new Date();
      var yearMatch = selectedPeriod.match(/^(\d{4})/);
      var selYear = yearMatch ? Number(yearMatch[1]) : today.getFullYear();

      var toDateNum = function(y, m, d) { return y * 10000 + m * 100 + d; };
      var ytdCutoffNum = toDateNum(selYear, today.getMonth() + 1, today.getDate());

      var getLeadDateNum = function(raw) {
        if (!raw) return null;
        var str = String(raw).trim();

        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
          var parts = str.split("-").map(Number);
          return toDateNum(parts[0], parts[1], parts[2]);
        }

        var dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
        if (dmyMatch) {
          var p1 = Number(dmyMatch[1]), p2 = Number(dmyMatch[2]), yr = Number(dmyMatch[3]);
          var day, month;
          if (p1 > 12) { day = p1; month = p2; }
          else if (p2 > 12) { month = p1; day = p2; }
          else { day = p1; month = p2; }
          return toDateNum(yr, month, day);
        }

        var parsed = new Date(str);
        if (!isNaN(parsed.getTime())) {
          return toDateNum(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
        }
        return null;
      };

      ytdOnly = allYearLeads.filter(function(lead) {
        var num = getLeadDateNum(lead.LeadDate);
        return num != null && num <= ytdCutoffNum;
      });
    }

    if (showTable === "Actual") {
      var selectedPeriodForActualYtd = String((selectedYears && selectedYears[0]) || "").trim();
      var yearOnlyMatchForActualYtd = selectedPeriodForActualYtd.match(/^(\d{4})$/);
      var selectedYearForActualYtd = yearOnlyMatchForActualYtd
        ? Number(yearOnlyMatchForActualYtd[1])
        : null;
      var isCurrentYearOnlyActual =
        selectedYearForActualYtd != null &&
        selectedYearForActualYtd === new Date().getFullYear();

      // Year-only: YTD Gross Profit Actual = Total (ERP uses GP-filtered totals, not P&L table).
      if (
        yearOnlyMatchForActualYtd &&
        useActualPlTableTotalsForGp
      ) {
        var vPl = Number(actualPlCardTotals.valueSum) || 0;
        var cPl = Number(actualPlCardTotals.costSum) || 0;
        var gpPl =
          actualPlCardTotals.grossProfitSum != null &&
          Number.isFinite(Number(actualPlCardTotals.grossProfitSum))
            ? Number(actualPlCardTotals.grossProfitSum)
            : vPl - cPl;
        setYTDtotalValueCurrent(vPl);
        setYTDtotalProductCostCurrent(cPl);
        setYTDtotalGrossProfitCurrent(gpPl);
        return;
      }

      if (
        isCurrentYearOnlyActual &&
        totalValue !== "" &&
        totalValue !== null &&
        totalValue !== undefined &&
        totalProductCost !== undefined &&
        totalProductCost !== null &&
        totalGrossProfit !== undefined &&
        totalGrossProfit !== null
      ) {
        setYTDtotalValueCurrent(Number(totalValue) || 0);
        setYTDtotalProductCostCurrent(Number(totalProductCost) || 0);
        setYTDtotalGrossProfitCurrent(Number(totalGrossProfit) || 0);
        return;
      }

      // Month/quarter fallback: sum GP-filtered rows in the YTD window (ERP uses live P&L Value/Cost).
      if (!getRowKey) return;

      var ytdActualRows = isErpPackage
        ? (typeof shouldShowActualPlRow === "function"
            ? ytdOnly.filter(function(r) { return shouldShowActualPlRow(r); })
            : ytdOnly)
        : ytdOnly.filter(function(lead) {
            var cost = getErpActualCostForRow(lead);
            return cost !== 0 || isDirectWonLead(lead);
          });

      if (ytdActualRows.length === 0) return;

      var totalQV = 0;
      var totalAC = 0;
      ytdActualRows.forEach(function(r) {
        totalQV += getErpActualQuotedValueForRow(r);
        totalAC += getErpActualCostForRow(r);
      });

      setYTDtotalValueCurrent(totalQV);
      setYTDtotalProductCostCurrent(totalAC);
      setYTDtotalGrossProfitCurrent(totalQV - totalAC);
      return;
    }

    if (isCrmPackage) {
      var ytdTotalQuotedValueCrm = 0;
      var ytdTotalQuotedExpensesCrm = 0;

      ytdOnly.forEach(function(r) {
        ytdTotalQuotedValueCrm += getCrmQuotedValue(r);
        if (typeof erpPlShowQuotedExpenseForRow === "function" && getRowKey) {
          ytdTotalQuotedExpensesCrm += parsePlExpenseStr(erpPlShowQuotedExpenseForRow(getRowKey(r)));
        } else {
          var expenseVal = getCrmQuotedExpense(r);
          if (expenseVal > 0) ytdTotalQuotedExpensesCrm += expenseVal;
        }
      });

      var ytdGrossProfitCrm = ytdTotalQuotedValueCrm - ytdTotalQuotedExpensesCrm;
      setYTDtotalValueCurrent(ytdTotalQuotedValueCrm);
      setYTDtotalProductCostCurrent(ytdTotalQuotedExpensesCrm);
      setYTDtotalGrossProfitCurrent(ytdGrossProfitCrm);

      // Validation rule: YTD Gross Profit must always be YTD Value - YTD Cost.
      if (Math.round(ytdGrossProfitCrm) !== Math.round(ytdTotalQuotedValueCrm - ytdTotalQuotedExpensesCrm)) {
        console.warn("[CRM GP VALIDATION] YTD Gross Profit mismatch detected.");
      }
      return;
    }

    var ytdLeads = (getProjectCost && getRowKey)
      ? ytdOnly.filter(function(lead) { return getProjectCost(getRowKey(lead)) !== 0; })
      : [];

    if (ytdLeads.length === 0) return;

    var ytdTotalQV = 0;
    var ytdTotalC = 0;
    const parsePlExpenseStrLocal = function(s) {
      return parseFloat(String(s || "0").replace(/[^\d.-]/g, "")) || 0;
    };
    ytdLeads.forEach(function(r) {
      var rk = getRowKey(r);
      ytdTotalQV += parseNum(r.quotedPrice || r.QuotedValue);
      ytdTotalC +=
        typeof erpPlShowQuotedExpenseForRow === "function"
          ? parsePlExpenseStrLocal(erpPlShowQuotedExpenseForRow(rk))
          : getProjectCost(rk);
    });

    setYTDtotalValueCurrent(ytdTotalQV);
    setYTDtotalProductCostCurrent(ytdTotalC);
    setYTDtotalGrossProfitCurrent(ytdTotalQV - ytdTotalC);

  }, [
    showTable,
    packages,
    isCrmPackage,
    quotationProjects,
    actualQuotationProjects,
    averagecost,
    perdiem,
    filteredLeadByYear,
    searchedLeadReports,
    searchedLeadReportswithoutyears,
    selectedYears,
    getProjectCost,
    getActualProjectCost,
    getRowKey,
    erpPlShowQuotedExpenseForRow,
    erpPlValueSumForCard,
    erpPlCostSumForCard,
    erpPlGrossProfitSumForCard,
    totalValue,
    totalProductCost,
    totalGrossProfit,
    getCrmQuotedExpense,
    getCrmQuotedValue,
    plGetQuotedExpenseForRow,
    plGetQuotedValueForRow,
    actualPlCardTotals,
    isErpPackage,
    useActualPlTableTotalsForGp,
    getErpActualQuotedValueForRow,
    getErpActualCostForRow,
    shouldShowActualPlRow,
    erpActualValueSumForCard,
    erpActualCostSumForCard,
    erpActualGrossProfitSumForCard,
  ]);

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setIsDropdownVisible(false); // Close the popup if clicked outside
    }
  };
  useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownVisible]);

  /** P&L Quoted Price column total — render source of truth (props), not internal state (avoids ~64L bleed-through). */
  const pandlQuotedPriceTotalDisplay = useMemo(() => {
    if (showTable === "Actual") return null;
    // If Gross Profit module has a per-row quoted-value accessor, Value must come from rows,
    // never from any P&L/global totals (prevents fallback to huge dashboard values).
    if (typeof plGetQuotedValueForRow === "function") return null;
    if (isCrmPackage) return null;
    const raw =
      erpPlValueSumForCard !== undefined && erpPlValueSumForCard !== null
        ? erpPlValueSumForCard
        : erpPlQuotedTotalsForGrossProfitCard?.valueSum;
    if (raw == null || !Number.isFinite(Number(raw))) return null;
    return Number(raw);
  }, [showTable, isCrmPackage, plGetQuotedValueForRow, erpPlValueSumForCard, erpPlQuotedTotalsForGrossProfitCard]);

  const pandlQuotedCostTotalDisplay = useMemo(() => {
    if (showTable === "Actual") return null;
    if (isCrmPackage) return null;
    const raw =
      erpPlCostSumForCard !== undefined && erpPlCostSumForCard !== null
        ? erpPlCostSumForCard
        : erpPlQuotedTotalsForGrossProfitCard?.costSum;
    if (raw == null || !Number.isFinite(Number(raw))) return null;
    return Number(raw);
  }, [showTable, isCrmPackage, erpPlCostSumForCard, erpPlQuotedTotalsForGrossProfitCard]);

  /** Σ P&L "Gross Profit" column (same as grid), not Value−Cost when itemized GP differs. */
  const pandlGrossProfitTotalDisplay = useMemo(() => {
    if (showTable === "Actual") return null;
    if (isCrmPackage) return null;
    const raw =
      erpPlGrossProfitSumForCard !== undefined && erpPlGrossProfitSumForCard !== null
        ? erpPlGrossProfitSumForCard
        : erpPlQuotedTotalsForGrossProfitCard?.grossProfitSum;
    if (raw == null || !Number.isFinite(Number(raw))) return null;
    return Number(raw);
  }, [showTable, isCrmPackage, erpPlGrossProfitSumForCard, erpPlQuotedTotalsForGrossProfitCard]);

  // HARD OVERRIDE (CRM Subscription Package):
  // Total Gross Profit card MUST come ONLY from "CRM Subscription" leads of the selected YEAR.
  // No P&L totals, no dashboard totals, no other memo/state allowed.
  const crmSubscriptionTotalGrossProfit = useMemo(() => {
    if (!isCrmPackage || showTable === "Actual") return null;

    // For CRM, `searchedLeadReports` is already the Gross Profit section's final year-filtered rows.
    // Using it directly ensures totals match the visible "Quoted Price" column values for the selected year.

    const isCrmSubscriptionLead = (lead) => {
      const pkg =
        lead?.packageType ??
        lead?.package_name ??
        lead?.serviceType ??
        lead?.package ??
        "";
      const s = String(pkg || "").trim().toLowerCase();
      // Be permissive: some APIs don't send explicit packageType per lead in CRM package.
      // If empty, treat it as CRM Subscription.
      if (!s) return true;
      return s === "crm subscription" || (s.includes("crm") && s.includes("subscription"));
    };

    const totalGrossProfitLeads = Array.isArray(searchedLeadReports)
      ? searchedLeadReports.filter((lead) => isCrmSubscriptionLead(lead))
      : [];

    // STEP 1/3 — DEBUG the real lead keys
    // eslint-disable-next-line no-console
    console.log("CRM LEAD OBJECT", totalGrossProfitLeads);
    // eslint-disable-next-line no-console
    console.log("FILTERED TOTAL LEADS", totalGrossProfitLeads);
    // eslint-disable-next-line no-console
    console.log("TOTAL ROW SAMPLE", totalGrossProfitLeads?.[0]);
    // eslint-disable-next-line no-console
    console.log("TABLE ROW SAMPLE => ", totalGrossProfitLeads?.[0]);

    // STEP 2 — SAFE HARD FIX (exact mapping)
    const totalValue = totalGrossProfitLeads.reduce((sum, lead) => {
      // CRM Value must match the P&L "Quoted Price" column exactly when accessor is provided.
      if (typeof plGetQuotedValueForRow === "function") {
        return sum + (Number(plGetQuotedValueForRow(lead)) || 0);
      }

      const raw =
        lead?.quotedPrice ??
        lead?.quoted_price ??
        lead?.quoted_price_display ??
        lead?.quotedPriceDisplay ??
        lead?.price ??
        lead?.QuotedPrice ??
        lead?.QuotedValue ??
        0;

      const cleanedValue =
        Number(
          String(raw ?? "0")
            .replace(/₹/g, "")
            .replace(/,/g, "")
            .replace(/[^\d.-]/g, "")
            .trim()
        ) || 0;

      return sum + cleanedValue;
    }, 0);

    const totalCost = totalGrossProfitLeads.reduce((sum, lead) => {
      if (typeof plGetQuotedExpenseForRow === "function") return sum + (Number(plGetQuotedExpenseForRow(lead)) || 0);
      const expense =
        Number(
          lead?.quoted_expenses ??
            lead?.quotedExpenses ??
            lead?.expense ??
            lead?.expenses ??
            lead?.cost ??
            0
        ) || 0;
      return sum + expense;
    }, 0);

    const totalGrossProfit = totalValue - totalCost;

    // DEBUG CHECK (requested)
    // eslint-disable-next-line no-console
    console.log({ totalValue, totalCost, totalGrossProfit });

    return { totalValue, totalCost, totalGrossProfit, count: totalGrossProfitLeads.length };
  }, [isCrmPackage, showTable, searchedLeadReports, plGetQuotedValueForRow, plGetQuotedExpenseForRow]);

  // FINAL HARD VALUE OVERRIDE (CRM only): compute Value (₹) ONLY from quoted price-like fields
  // on the Gross Profit section rows. This is used ONLY for rendering Value rows (Total + YTD),
  // so it cannot be overridden by any other totals/state.
  const calculatedQuotedPriceTotal = useMemo(() => {
    const rows = Array.isArray(searchedLeadReports) ? searchedLeadReports : [];

    const total = rows.reduce((sum, row) => {
      if (typeof plGetQuotedValueForRow === "function") {
        return sum + (Number(plGetQuotedValueForRow(row)) || 0);
      }

      const rawQuotedPrice =
        row?.quotedPrice ??
        row?.quoted_price ??
        row?.quoted_price_display ??
        row?.quotedPriceDisplay ??
        row?.price ??
        "0";

      const parsedQuotedPrice =
        parseFloat(
          String(rawQuotedPrice)
            .replace(/₹/g, "")
            .replace(/,/g, "")
            .trim()
        ) || 0;

      return sum + parsedQuotedPrice;
    }, 0);

    // eslint-disable-next-line no-console
    console.log("FINAL QUOTED TOTAL", total);
    return total;
  }, [searchedLeadReports, plGetQuotedValueForRow]);

  const calculatedYTDQuotedPriceTotal = useMemo(() => {
    if (!isCrmPackage) return null;
    const selectedPeriod = String((selectedYears && selectedYears[0]) || "").trim();
    const m = selectedPeriod.match(/^(\d{4})$/);
    const selectedYear = m ? Number(m[1]) : null;
    if (!selectedYear) return null;

    const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    const cutoff = selectedYear === 2025 ? endOfDay(new Date(2025, 4, 8)) : null; // 8 May 2025

    const getLeadDate = (row) => {
      const raw =
        row?.lead_date ||
        row?.leadDate ||
        row?.LeadDate ||
        row?.leadDateTime ||
        row?.created_at ||
        row?.createdAt;
      if (!raw) return null;
      const normalized = ValidDate(raw);
      if (normalized) {
        const [y, mm, dd] = normalized.split("-").map(Number);
        const dt = new Date(y, mm - 1, dd);
        return isNaN(dt.getTime()) ? null : dt;
      }
      const dt = new Date(raw);
      return isNaN(dt.getTime()) ? null : dt;
    };

    const ytdRows = (Array.isArray(searchedLeadReportswithoutyears) ? searchedLeadReportswithoutyears : []).filter((row) => {
      const dt = getLeadDate(row);
      if (!dt) return false;
      if (dt.getFullYear() !== selectedYear) return false;
      if (cutoff) return dt <= cutoff;
      return true;
    });

    const total = ytdRows.reduce((sum, row) => {
      if (typeof plGetQuotedValueForRow === "function") {
        return sum + (Number(plGetQuotedValueForRow(row)) || 0);
      }

      const rawQuotedPrice =
        row?.quotedPrice ??
        row?.quoted_price ??
        row?.quoted_price_display ??
        row?.quotedPriceDisplay ??
        row?.price ??
        "0";

      const parsedQuotedPrice =
        parseFloat(
          String(rawQuotedPrice)
            .replace(/₹/g, "")
            .replace(/,/g, "")
            .trim()
        ) || 0;

      return sum + parsedQuotedPrice;
    }, 0);

    return total;
  }, [isCrmPackage, selectedYears, searchedLeadReportswithoutyears, plGetQuotedValueForRow]);

  const displayValueRow =
    pandlQuotedPriceTotalDisplay != null
      ? pandlQuotedPriceTotalDisplay
      : (crmSubscriptionTotalGrossProfit ? crmSubscriptionTotalGrossProfit.totalValue : totalValue);
  const displayCostRow =
    pandlQuotedCostTotalDisplay != null
      ? pandlQuotedCostTotalDisplay
      : (crmSubscriptionTotalGrossProfit ? crmSubscriptionTotalGrossProfit.totalCost : totalProductCost);
  const displayGpRow =
    pandlGrossProfitTotalDisplay != null
      ? pandlGrossProfitTotalDisplay
      : (crmSubscriptionTotalGrossProfit
        ? crmSubscriptionTotalGrossProfit.totalGrossProfit
        : (pandlQuotedPriceTotalDisplay != null && pandlQuotedCostTotalDisplay != null
          ? pandlQuotedPriceTotalDisplay - pandlQuotedCostTotalDisplay
          : totalGrossProfit));

  // HARD FINAL BINDINGS (TOTAL GROSS PROFIT CARD ONLY):
  // CRM: prefer FinanceGraph year totals (erpPl*) / state from applyPandlTotalsToCard —
  // those match P&L Quoted Price without using inflated lead.QuotedValue.
  const totalValueFinal =
    isCrmPackage &&
    erpPlValueSumForCard != null &&
    Number.isFinite(Number(erpPlValueSumForCard))
      ? Number(erpPlValueSumForCard)
      : isCrmPackage && crmSubscriptionTotalGrossProfit
        ? (calculatedQuotedPriceTotal ?? crmSubscriptionTotalGrossProfit.totalValue)
        : displayValueRow;
  const totalCostFinal =
    isCrmPackage &&
    erpPlCostSumForCard != null &&
    Number.isFinite(Number(erpPlCostSumForCard))
      ? Number(erpPlCostSumForCard)
      : isCrmPackage && crmSubscriptionTotalGrossProfit
        ? crmSubscriptionTotalGrossProfit.totalCost
        : displayCostRow;
  const totalGrossProfitFinal =
    isCrmPackage &&
    erpPlGrossProfitSumForCard != null &&
    Number.isFinite(Number(erpPlGrossProfitSumForCard))
      ? Number(erpPlGrossProfitSumForCard)
      : isCrmPackage && crmSubscriptionTotalGrossProfit
        ? crmSubscriptionTotalGrossProfit.totalGrossProfit
        : displayGpRow;

  // eslint-disable-next-line no-console
  console.log("TOTAL VALUE FINAL", totalValueFinal);
  // eslint-disable-next-line no-console
  console.log("TOTAL COST FINAL", totalCostFinal);
  // eslint-disable-next-line no-console
  console.log("TOTAL GP FINAL", totalGrossProfitFinal);

  const totalGrossProfitChartData = useMemo(() => {
    return [
      { name: "Total Cost", value: Math.round(Number(totalCostFinal) || 0) },
      { name: "Total Gross Profit", value: Math.round(Number(totalGrossProfitFinal) || 0) },
    ];
  }, [totalCostFinal, totalGrossProfitFinal]);

  // ERP: when the selected filter set yields zero rows, show "No data available" (avoid rendering 0-value graphs).
  const nonCrmHasLeadsForTotal = !isCrmPackage && Array.isArray(searchedLeadReports) && searchedLeadReports.length > 0;
  const hasPandlCardTotals =
    showTable !== "Actual" &&
    pandlQuotedPriceTotalDisplay != null &&
    Number.isFinite(pandlQuotedPriceTotalDisplay);
  const hasActualPlCardTotals = useActualPlTableTotalsForGp;

  const selectedPeriodForYtdFinal = String((selectedYears && selectedYears[0]) || "").trim();
  const yearOnlyMatchForYtdFinal = selectedPeriodForYtdFinal.match(/^(\d{4})$/);
  const selectedYearForYtdFinal = yearOnlyMatchForYtdFinal
    ? Number(yearOnlyMatchForYtdFinal[1])
    : null;
  const isCurrentYearOnlyForYtdFinal =
    selectedYearForYtdFinal != null &&
    selectedYearForYtdFinal === new Date().getFullYear();

  const ytdValueFinal = useMemo(() => {
    if (showTable === "Actual" && hasActualPlCardTotals && yearOnlyMatchForYtdFinal) {
      return Number(actualPlCardTotals.valueSum) || 0;
    }
    // CRM: mirror Total Gross Profit Value (filter-independent year totals).
    if (
      isCrmPackage &&
      showTable !== "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      selectedYearForYtdFinal !== 2025 &&
      erpPlValueSumForCard != null &&
      Number.isFinite(Number(erpPlValueSumForCard))
    ) {
      return Number(erpPlValueSumForCard);
    }
    if (
      showTable !== "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      pandlQuotedPriceTotalDisplay != null
    ) {
      return pandlQuotedPriceTotalDisplay;
    }
    if (
      showTable === "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      totalValue !== "" &&
      totalValue != null
    ) {
      return Number(totalValue) || 0;
    }
    return Number(ytdtotalValueCurrent) || 0;
  }, [
    showTable,
    hasActualPlCardTotals,
    yearOnlyMatchForYtdFinal,
    actualPlCardTotals,
    isCrmPackage,
    isCurrentYearOnlyForYtdFinal,
    selectedYearForYtdFinal,
    erpPlValueSumForCard,
    pandlQuotedPriceTotalDisplay,
    totalValue,
    ytdtotalValueCurrent,
  ]);

  const ytdCostFinal = useMemo(() => {
    if (showTable === "Actual" && hasActualPlCardTotals && yearOnlyMatchForYtdFinal) {
      return Number(actualPlCardTotals.costSum) || 0;
    }
    if (
      isCrmPackage &&
      showTable !== "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      selectedYearForYtdFinal !== 2025 &&
      erpPlCostSumForCard != null &&
      Number.isFinite(Number(erpPlCostSumForCard))
    ) {
      return Number(erpPlCostSumForCard);
    }
    if (
      showTable !== "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      pandlQuotedCostTotalDisplay != null
    ) {
      return pandlQuotedCostTotalDisplay;
    }
    if (
      showTable === "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      totalProductCost !== undefined &&
      totalProductCost !== null
    ) {
      return Number(totalProductCost) || 0;
    }
    return Number(ytdtotalProductCostCurrent) || 0;
  }, [
    showTable,
    hasActualPlCardTotals,
    yearOnlyMatchForYtdFinal,
    actualPlCardTotals,
    isCrmPackage,
    isCurrentYearOnlyForYtdFinal,
    selectedYearForYtdFinal,
    erpPlCostSumForCard,
    pandlQuotedCostTotalDisplay,
    totalProductCost,
    ytdtotalProductCostCurrent,
  ]);

  const ytdGrossProfitFinal = useMemo(() => {
    if (showTable === "Actual" && hasActualPlCardTotals && yearOnlyMatchForYtdFinal) {
      if (
        actualPlCardTotals.grossProfitSum != null &&
        Number.isFinite(Number(actualPlCardTotals.grossProfitSum))
      ) {
        return Number(actualPlCardTotals.grossProfitSum);
      }
      return ytdValueFinal - ytdCostFinal;
    }
    if (
      isCrmPackage &&
      showTable !== "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      selectedYearForYtdFinal !== 2025 &&
      erpPlGrossProfitSumForCard != null &&
      Number.isFinite(Number(erpPlGrossProfitSumForCard))
    ) {
      return Number(erpPlGrossProfitSumForCard);
    }
    if (
      showTable !== "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      pandlGrossProfitTotalDisplay != null
    ) {
      return pandlGrossProfitTotalDisplay;
    }
    if (
      showTable === "Actual" &&
      isCurrentYearOnlyForYtdFinal &&
      totalGrossProfit !== undefined &&
      totalGrossProfit !== null
    ) {
      return Number(totalGrossProfit) || 0;
    }
    return Number(ytdtotalGrossProfitCurrent) || 0;
  }, [
    showTable,
    hasActualPlCardTotals,
    yearOnlyMatchForYtdFinal,
    actualPlCardTotals,
    isCrmPackage,
    isCurrentYearOnlyForYtdFinal,
    selectedYearForYtdFinal,
    erpPlGrossProfitSumForCard,
    pandlGrossProfitTotalDisplay,
    totalGrossProfit,
    ytdtotalGrossProfitCurrent,
    ytdValueFinal,
    ytdCostFinal,
  ]);

  const data2 = useMemo(
    () => [
      { name: "YTD Cost", value: Math.round(Number(ytdCostFinal)) || 0 },
      { name: "YTD Gross Profit", value: Math.round(Number(ytdGrossProfitFinal)) || 0 },
    ],
    [ytdCostFinal, ytdGrossProfitFinal]
  );

  const hasTotalData =
    hasPandlCardTotals ||
    hasActualPlCardTotals ||
    (((isCrmPackage ? (crmSubscriptionTotalGrossProfit?.count > 0) : nonCrmHasLeadsForTotal) &&
      totalValue !== "" &&
      totalValue !== null &&
      totalValue !== undefined &&
      totalProductCost !== undefined &&
      totalProductCost !== null &&
      totalGrossProfit !== undefined &&
      totalGrossProfit !== null));

  const hasYtdDataFromState =
    ytdtotalValueCurrent !== "" &&
    ytdtotalValueCurrent !== null &&
    ytdtotalValueCurrent !== undefined &&
    ytdtotalProductCostCurrent !== undefined &&
    ytdtotalProductCostCurrent !== null &&
    ytdtotalGrossProfitCurrent !== undefined &&
    ytdtotalGrossProfitCurrent !== null &&
    (Number(ytdtotalProductCostCurrent) !== 0 ||
      Number(ytdtotalGrossProfitCurrent) !== 0 ||
      Number(ytdtotalValueCurrent) !== 0);

  const hasYtdPandlCardTotals =
    showTable !== "Actual" &&
    isCurrentYearOnlyForYtdFinal &&
    pandlQuotedPriceTotalDisplay != null &&
    Number.isFinite(pandlQuotedPriceTotalDisplay);

  const hasYtdActualPlCardTotals =
    showTable === "Actual" &&
    yearOnlyMatchForYtdFinal &&
    hasActualPlCardTotals;

  const hasYtdData =
    hasYtdPandlCardTotals ||
    hasYtdActualPlCardTotals ||
    hasYtdDataFromState ||
    Number(ytdCostFinal) !== 0 ||
    Number(ytdGrossProfitFinal) !== 0 ||
    Number(ytdValueFinal) !== 0;

  useEffect(() => {
    if (filterVersion == null) return;
    setProbabilityTab("graph");
    setYTD("graph");
  }, [filterVersion]);

  useEffect(() => {
    if (totalGrossProfitChartData.some((entry) => entry.value < 0)) setProbabilityTab("table");
  }, [totalGrossProfitChartData]);

  const COLORS = [
    "#0da2e7",
    "#3c83f6",
    "#64a4f8",
    "#92c2fc",
    "#c6defe",
    "#a4d3f3",
    "#7fbff5",
    "#539af8",
    "#2e6ff3",
    "#1549e0",
  ];

  // const renderCustomLabel = ({
  //   cx,
  //   cy,
  //   midAngle,
  //   innerRadius,
  //   outerRadius,
  //   index,
  // }) => {
  //   const RADIAN = Math.PI / 180;
  //   const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  //   const x = cx + radius * Math.cos(-midAngle * RADIAN);
  //   const y = cy + radius * Math.sin(-midAngle * RADIAN);

  //   const entry = data[index]; // Access the correct entry for the index
  //   const formattedValue = `${currency}${USAComma(entry.value)}`;
  //   const fontSize = Math.max(10, (outerRadius - innerRadius) * 0.1);

  //   return (
  //     <text
  //       x={x}
  //       y={y}
  //       textAnchor="middle"
  //       dominantBaseline="middle"
  //       fontSize={fontSize}
  //       fill="#000"
  //       fontWeight="bold"
  //     >
  //       <tspan x={x} dy="-0.4em">
  //         {entry.name.trim()}
  //       </tspan>
  //       <tspan x={x} dy="1.2em">
  //         {formattedValue}
  //       </tspan>
  //     </text>
  //   );
  // };

  // const renderCustomLabel2 = ({
  //   cx,
  //   cy,
  //   midAngle,
  //   innerRadius,
  //   outerRadius,
  //   index,
  // }) => {
  //   const RADIAN = Math.PI / 180;
  //   const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  //   const x = cx + radius * Math.cos(-midAngle * RADIAN);
  //   const y = cy + radius * Math.sin(-midAngle * RADIAN);

  //   const entry = data2[index]; // make sure you're using the right dataset
  //   const formattedValue = `${currency}${USAComma(entry.value)}`;

  //   // Font size can be dynamic based on radius or container size
  //   const fontSize = Math.max(10, (outerRadius - innerRadius) * 0.1);

  //   return (
  //     <text
  //       x={x}
  //       y={y}
  //       textAnchor="middle"
  //       dominantBaseline="middle"
  //       fontSize={fontSize}
  //       fill="#000"
  //       fontWeight="bold"
  //     >
  //       <tspan x={x} dy="-0.4em">
  //         {entry.name.trim()}
  //       </tspan>
  //       <tspan x={x} dy="1.2em">
  //         {formattedValue}
  //       </tspan>
  //     </text>
  //   );
  // };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value } = payload[0];
      const formattedValue = `${currency}${USAComma(value)}`;
      return (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ccc",
            padding: "8px",
            borderRadius: "6px",
            boxShadow: "0px 0px 8px rgba(0,0,0,0.1)",
          }}
        >
          <p>
            <strong>{name}</strong>
          </p>
          <p>Quoted Value: {formattedValue}</p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="" style={{ marginTop: 6 }}>
        {payload.map((entry, index) => (
          <div
            key={`legend-${index}`}
            className="flex items-start"
          >
            <div
              className="w-[10px] h-[10px] rounded-full mr-2 mt-1"
              style={{ backgroundColor: entry.color }}
            />

            <span className="text-xs w-[50%] text-black break-words whitespace-normal">
              {entry.value} ({currency})
            </span>

            <span className="w-[50%] text-sm font-semibold text-start break-all whitespace-normal">
              {USAComma(entry.payload.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };
  const CustomLegendGrossProfitYTD = ({ payload }) => {
    return (
      <div className="" style={{ marginTop: 6 }}>
        {payload.map((entry, index) => (
          <div
            key={`legend-${index}`}
            className="flex items-start"
          >
            <div
              className="w-[10px] h-[10px] rounded-full mr-2  mt-1"
              style={{ backgroundColor: entry.color }}
            />

            <span className="text-xs w-[50%] text-black break-words whitespace-normal">
              {entry.value} ({currency})
            </span>

            <span className="w-[50%] text-sm font-semibold text-start break-all whitespace-normal">
              {USAComma(entry.payload.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };


  return (
    <div className="flex w-full h-full gap-2 ">
      {process.env.NODE_ENV !== "production" && (
        <div style={{ display: "none" }}>
          {JSON.stringify((Array.isArray(searchedLeadReports) ? searchedLeadReports[0] : null) || null)}
        </div>
      )}
      {(canViewTotalGrossProfitGraph || canViewTotalGrossProfitTable) && (
      <div className="h-full w-1/2 bg-white rounded-md z-10 px-2 pt-1">
        <div className="h-[10%]">
          <div className="flex flex-wrap justify-start items-center xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
            <div className="">
              <h2 className="text-lg font-semibold sm:text-sm md:text-sm lg:text-lg">
                Total Gross Profit
              </h2>
            </div>

            <div className="md:flex lg:ml-8 lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end ">
              <div className="show-potential-download lg:justify-end">
                <div className="flex items-center justify-between text-3xl sm:text-xl md:text-md">
                  {probabilityTab === "table" && canViewTotalGrossProfitGraph && (
                    <span title="Graph">
                      <ChartColumnDecreasing
                        className="cursor-pointer text-blue-600"
                        onClick={() => setProbabilityTab("graph")}
                        fontSize="2.5rem"
                      />
                    </span>
                  )}
                  {probabilityTab === "graph" && canViewTotalGrossProfitTable && (
                    <span title="Table">
                      <TableOfContents
                        className="cursor-pointer text-blue-600"
                        onClick={() => setProbabilityTab("table")}
                        fontSize="2.5rem"
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="h-[14rem] flex">
          {(probabilityTab === "table" && canViewTotalGrossProfitTable) || !canViewTotalGrossProfitGraph ? (
            hasTotalData ? (
              <div className="w-full pt-3">
                <div className=" border-black border">
                  <div className="w-full flex ">
                    <div className="w-[60%] text-start font-semibold bg-[#e8e8e8] text-black border-b border-black px-2 py-1">
                      <p className="text-xs break-words whitespace-normal">Value ({currency})</p>
                    </div>
                    <div className="w-[40%] text-start border-b border-black px-2 py-1">
                      <p className="text-xs break-all whitespace-normal flex justify-start items-center">
                        {USAComma(
                          isCrmPackage
                            ? totalValueFinal
                            : typeof plGetQuotedValueForRow === "function"
                              ? (calculatedQuotedPriceTotal ?? totalValueFinal)
                              : totalValueFinal
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="w-full flex ">
                    <div className="w-[60%] text-start font-semibold bg-[#e8e8e8] border-b border-black text-black  px-2 py-1">
                      <p className="text-xs break-words whitespace-normal">Cost ({currency})</p>
                    </div>
                    <div className="w-[40%] text-start border-b border-black px-2 py-1 ">
                      <p className="text-xs break-all whitespace-normal flex justify-start items-center"> {USAComma(totalCostFinal)}</p>
                    </div>
                  </div>
                  <div className="w-full flex ">
                    <div className="w-[60%] text-start font-semibold bg-[#e8e8e8] text-black  px-2 py-1">
                      <p className="text-xs break-words whitespace-normal">Gross Profit ({currency})</p>
                    </div>
                    <div className="w-[40%] text-start  px-2 py-1">
                      <p className="text-xs break-all whitespace-normal flex justify-start items-center"> {USAComma(totalGrossProfitFinal)}</p>
                    </div>
                  </div>

                </div>
              </div>
            ) : (
              <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                <p className="noDataAvailable">No data available</p>{" "}
              </div>
            )
          ) : (canViewTotalGrossProfitGraph && hasTotalData) ? (
            <div className="relative w-full flex justify-center items-center">
              <>
                <div className="w-full h-[220px] cp:h-[360px] ">
                  {/* <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data}
                          cx="50%"
                          cy="50%"
                          innerRadius={0}
                          fill="#8884d8"
                          dataKey="value"
                          outerRadius="90%"   // 👈 ADD THIS
                          paddingAngle={0}
                          startAngle={90}
                          endAngle={-270}
                          label={renderCustomLabel}
                          labelLine={false}
                        >
                          {data.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                              stroke="#000"
                              strokeWidth={1}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer> */}
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={totalGrossProfitChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius="55%"
                        outerRadius="90%"
                        dataKey="value"
                      >
                        {totalGrossProfitChartData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                        <Label
                          value="Total Cost"
                          position="center"
                          dy={-4}
                          fill="#000000"
                          style={{ fontSize: 10, fontWeight: 600 }}

                        />

                        <Label
                          value={`${currency}${formatNumberByCountry(totalCostFinal)}`}
                          position="center"
                          dy={10}
                          fill="#000000"
                          style={{ fontSize: 13, fontWeight: 600, color: "#00000" }}
                        />

                      </Pie>

                      <Tooltip content={<CustomTooltip />} />
                      <Legend content={<CustomLegend />} />
                    </PieChart>
                  </ResponsiveContainer>

                </div>
              </>
            </div>
          ) : (
            <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
              <p className="noDataAvailable">No data available</p>{" "}
            </div>
          )}
        </div>
      </div>
      )}

      {(canViewYtdGrossProfitGraph || canViewYtdGrossProfitTable) && (
      <div className="h-full w-1/2 bg-white rounded-md px-2 pt-1">
        <div className="h-[10%]">
          <div className="flex flex-wrap justify-start items-center xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
            <div>
              <h2 className="text-lg font-semibold sm:text-sm md:text-sm lg:text-lg">
                YTD Gross Profit
              </h2>
            </div>

            <div className="md:flex lg:ml-8 lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end ">
              <div className="show-potential-download lg:justify-end">
                <div className=" flex items-center justify-between text-3xl sm:text-xl md:text-md">
                  {YTD === "table" && canViewYtdGrossProfitGraph && (
                    <span title="Graph">
                      <ChartColumnDecreasing
                        className="cursor-pointer text-blue-600"
                        onClick={() => setYTD("graph")}
                        fontSize="3rem"
                      />
                    </span>
                  )}
                  {YTD === "graph" && canViewYtdGrossProfitTable && (
                    <span title="Table">
                      <TableOfContents
                        className="cursor-pointer text-blue-600"
                        onClick={() => setYTD("table")}
                        fontSize="3rem"
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* <hr />
            <hr /> */}
        </div>

        <div className="h-[14rem] flex ">
          {(YTD === "table" && canViewYtdGrossProfitTable) || !canViewYtdGrossProfitGraph ? (
            hasYtdData ? (
              <div className="w-full pt-3">
                <div className="overflow-auto border border-black">
                  <div className="w-full flex">
                    <div className="w-[60%] text-start font-semibold bg-[#e8e8e8] text-black border-b border-black px-2 py-1">
                      <p className="text-xs break-words whitespace-normal">YTD Value ({currency})</p>
                    </div>
                    <div className="w-[40%] text-start border-b px-2 py-1 border-black">
                      <p className="text-xs break-all whitespace-normal flex justify-start items-center">
                        {USAComma(isCrmPackage ? ytdValueFinal : (calculatedYTDQuotedPriceTotal ?? ytdValueFinal))}
                      </p>

                    </div>
                  </div>
                  <div className="w-full flex">
                    <div className="w-[60%] text-start font-semibold bg-[#e8e8e8] text-black border-b border-black px-2 py-1">
                      <p className="text-xs break-words whitespace-normal">YTD Cost ({currency})</p>
                    </div>
                    <div className="w-[40%] text-start border-b px-2 py-1 border-black">
                      <p className="text-xs break-all whitespace-normal flex justify-start items-center">
                        {USAComma(ytdCostFinal)}
                      </p>
                    </div>
                  </div>

                  <div className="w-full flex">
                    <div className="w-[60%] text-start font-semibold bg-[#e8e8e8] text-black  px-2 py-1">
                      <p className="text-xs break-words whitespace-normal">YTD Gross Profit ({currency})</p>
                    </div>
                    <div className="w-[40%] text-start  px-2 py-1 ">
                      <p className="text-xs break-all whitespace-normal flex justify-start items-center">
                        {USAComma(ytdGrossProfitFinal)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                <p className="noDataAvailable ">No data available</p>
              </div>
            )
          ) : (canViewYtdGrossProfitGraph && hasYtdData) ? (
            <div className="relative w-full flex justify-center items-center">
              <>
                <div className="w-full h-[220px] cp:h-[360px]">
                    {/* <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={data2}
                            cx="50%"
                            cy="50%"
                            innerRadius={0}
                            fill="#8884d8"
                            dataKey="value"
                            outerRadius="90%"
                            startAngle={90}
                            endAngle={-270}
                            label={renderCustomLabel2}
                            labelLine={false}
                          >
                            {data2.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke="#000"
                                strokeWidth={1}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer> */}
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data2}
                          cx="50%"
                          cy="50%"
                          innerRadius="55%"
                          outerRadius="90%"
                          dataKey="value"
                        >
                          {data2.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                          <Label
                            value="YTD Cost"
                            position="center"
                            dy={-4}
                            fill="#000000"
                            style={{ fontSize: 10, fontWeight: 600 }}

                          />

                          <Label
                            value={`${currency}${formatNumberByCountry(ytdCostFinal)}`}
                            position="center"
                            dy={10}    // move up
                            fill="#000000"
                            style={{ fontSize: 13, fontWeight: 600, color: "#00000" }}
                          />
                        </Pie>

                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegendGrossProfitYTD />} />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* <div className="absolute bottom-2 flex gap-1">
                                                          <p>{currency}{USAComma(highestQuotedLead.QuotedValue)}</p>
                                                      </div> */}
                  </div>

                </>
            </div>
          ) : (
            <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
              <p className="noDataAvailable">No data available</p>{" "}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default SearchSalesGrossProfit;
